import React from 'react'

interface CheckAvailabilityData {
  // API response fields
  saasOrgId?: number;
  billStartDate?: string;
  billEndDate?: string;
  invoiceDate?: string;
  billedDays?: number;
  dailyRate?: number;
  weeklyRate?: number;
  monthlyRate?: number;
  engineRate?: number;
  engineDailyRate?: number;
  engineWeeklyRate?: number;
  engineMonthlyRate?: number;
  liabilityInsurancePerDayValue?: number | null;
  physicalInsurancePerDayValue?: number | null;
  physicalActualRate?: number | null;
  liabilityActualRate?: number | null;
  physicalActualTotal?: number | null;
  liabilityActualTotal?: number | null;
  physicalActualTax?: number | null;
  liabilityActualTax?: number | null;
  physicalInsuranceTotalDays?: number | null;
  liabilityInsuranceTotalDays?: number | null;
  totalDistance?: number;
  totalEngineHours?: number;
  lastOdometerReading?: number;
  currentOdometerReading?: number;
  lastEngineHours?: number;
  currentEngineHours?: number;
  totalDailyAmount?: number;
  totalDistanceAmount?: number | null;
  totalReeferAmount?: number;
  totalLiabilityInsurance?: number | null;
  totalPhysicalInsurance?: number | null;
  adminFees?: number | null;
  platformFees?: number;
  subTotalAmount?: number;
  taxDailyAmount?: number;
  taxDistanceAmount?: number | null;
  taxReeferAmount?: number;
  taxLiabilityInsurance?: number | null;
  taxPhysicalInsurance?: number | null;
  taxAdminFees?: number | null;
  taxPlatformFees?: number;
  platformFeesValue?: string;
  gst?: number;
  pst?: number;
  hst?: number;
  totalTaxes?: number;
  totalAmount?: number;
  currency?: string;
  plan?: string;
  distanceUOM?: string;
  distanceRate?: number;
  customFields?: {
    fieldName: string;
    amount: any;
    duration: string;
    billingLogic: string;
  }[];

  // Legacy / fallback fields
  startDate?: string;
  endDate?: string;
  additionalPriceExcessDays?: string;
  allowOffer?: string;
  isToOverride?: boolean;
  isEnableAnyAvailble?: string;
  arrivalParkingLotId?: number;
  departureParkingLotId?: number;
  distanceToTravel?: string | number;
  defaultCurrency?: string;
  estimatedDistancePerDay?: number;
  estimatedEngineHoursPerDay?: number;
  equipmentId?: number;
  estimatedDistance?: string | number;
  listingType?: string;
  loadType?: string;
  movePrice?: string;
  noOfDaysAllowed?: string;
  pricePerKm?: string | number;
  isInsurancePaidByOwnerLiability?: string;
  isInsurancePaidByOwnerPhysical?: string;
  isInsurancePaidByRenterLiability?: string;
  isInsurancePaidByRenterPhysical?: string;
  ratePlanIdLiability?: string;
  ratePlanIdPhysical?: string;
  contractNumber?: string;
  isDefaultInsuranceEnabled?: boolean;
  quantity?: string;
  useEquipmentPricing?: string;
  availabilityViewType?: string;
  distanceBetweenLots?: number;
  travelTimeBetweenLots?: number;
  renterName?: string;
  renterAddress?: string;
  unitNumber?: string;
  vin?: string;
  year?: number;
  equipmentType?: string;
  activationAddress?: string;
  deactivationAddress?: string;
  owner?: string;
}

interface Props {
  data?: CheckAvailabilityData | CheckAvailabilityData[];
}

const CheckAvailabilityPreview: React.FC<Props> = ({ data }) => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  // Handle both single invoice and array of invoices
  const invoices = Array.isArray(data) ? data : (data ? [data] : []);

  if (invoices.length === 0) {
    return <div>No invoice data available</div>;
  }

  // If only one invoice, render directly
  if (invoices.length === 1) {
    return <InvoiceComponent data={invoices[0]} invoiceIndex={0} totalInvoices={1} />;
  }

  // Multiple invoices - use accordion format
  return (
    <div className="accordion" id="invoiceAccordion">
      {invoices.map((invoice, index) => {
        const startLabel = invoice.billStartDate
          ? new Date(invoice.billStartDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        const endLabel = invoice.billEndDate
          ? new Date(invoice.billEndDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        const total = invoice.totalAmount != null
          ? `${invoice.currency ?? ''} ${Number(invoice.totalAmount).toFixed(2)}`
          : '';

        const isOpen = openIndex === index;
        return (
          <div
            className="accordion-item"
            key={index}
            style={{ borderRadius: 0, border: 'none', borderBottom: '1px solid #dee2e6' }}
          >
            <h2 className="accordion-header" id={`invoiceHeading${index}`} style={{ margin: 0 }}>
              <button
                className={`accordion-button${isOpen ? '' : ' collapsed'}`}
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-controls={`invoiceCollapse${index}`}
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '10px 16px',
                  background: '#e8f0fe',
                  boxShadow: 'none',
                  borderRadius: 0,
                }}
              >
                <span style={{ marginRight: '14px', color: '#555', minWidth: '80px' }}>
                  Invoice {index + 1}
                </span>
                <span style={{ color: '#333' }}>
                  {startLabel} &nbsp;→&nbsp; {endLabel}
                </span>
                {total && (
                  <span style={{ marginLeft: 'auto', marginRight: '32px', color: '#1a73e8', fontWeight: 700 }}>
                    {total}
                  </span>
                )}
              </button>
            </h2>
            <div
              className={`accordion-collapse collapse${isOpen ? ' show' : ''}`}
              id={`invoiceCollapse${index}`}
            >
              <div style={{ padding: 0, margin: 0 }}>
                <InvoiceComponent data={invoice} invoiceIndex={index} totalInvoices={invoices.length} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InvoiceComponent: React.FC<{ data: CheckAvailabilityData; invoiceIndex: number; totalInvoices: number }> = ({ data, invoiceIndex, totalInvoices }) => {
  const d = data;
  console.log(d, "d")
  const currency = d.currency ?? d.defaultCurrency ?? 'USD';

  const fmt = (n: number | null | undefined, ccy = currency) =>
    `${ccy} ${(Number(n ?? 0)).toFixed(2)}`;

  const fmtN = (n: number | null | undefined) =>
    (Number(n ?? 0)).toFixed(2);

  const formatDate = (s?: string) => {
    if (!s) return '';
    const dt = new Date(s);
    return dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatDateLong = (s?: string) => {
    if (!s) return '';
    const dt = new Date(s);
    return dt.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const calcDays = (a?: string, b?: string) => {
    if (!a || !b) return 0;
    return Math.ceil(Math.abs(new Date(b).getTime() - new Date(a).getTime()) / 86400000) + 1;
  };

  const startDate   = d.billStartDate ?? d.startDate;
  const endDate     = d.billEndDate   ?? d.endDate;
  const invoiceDate = d.invoiceDate   ?? new Date().toISOString();
  const billedDays  = d.billedDays    ?? calcDays(startDate, endDate);

  const totalDistance    = d.totalDistance    ?? 0;
  const totalEngineHours = d.totalEngineHours ?? 0;

  const avgDailyDist  = billedDays > 0 ? totalDistance    / billedDays : 0;
  const avgDailyHours = billedDays > 0 ? totalEngineHours / billedDays : 0;

  const getPlan = () => {
    const p = (d.plan ?? '').toLowerCase();
    if (p.includes('month')) return 'Month';
    if (p.includes('week'))  return 'Week';
    return 'Day';
  };
  const planLabel = getPlan();

  const rentalRate       = d.dailyRate ?? d.monthlyRate ?? 0;
  const totalDailyAmt    = d.totalDailyAmount  ?? 0;
  const totalDistAmt     = d.totalDistanceAmount ?? 0;
  const totalReeferAmt   = d.totalReeferAmount  ?? 0;
  const liabilityAmt     = d.totalLiabilityInsurance ?? 0;
  const physicalAmt      = d.totalPhysicalInsurance  ?? 0;
  const platformFeesAmt  = d.platformFees ?? 0;
  const subTotal         = d.subTotalAmount ?? 0;
  const totalTaxes       = d.totalTaxes ?? (Number(d.gst ?? 0) + Number(d.pst ?? 0) + Number(d.hst ?? 0));
  const totalAmount      = d.totalAmount ?? (subTotal + totalTaxes);

  const liabilityDays = d.liabilityInsuranceTotalDays ?? billedDays;
  const physicalDays  = d.physicalInsuranceTotalDays  ?? billedDays;
  const liabilityRate = d.liabilityActualRate ?? 0;
  const physicalRate  = d.physicalActualRate  ?? 0;

  const taxSubTotal = (d.taxDailyAmount ?? 0)
    + (Number(d.taxDistanceAmount ?? 0))
    + (d.taxReeferAmount ?? 0)
    + (Number(d.taxLiabilityInsurance ?? 0))
    + (Number(d.taxPhysicalInsurance ?? 0))
    + (Number(d.taxAdminFees ?? 0))
    + (d.taxPlatformFees ?? 0);

  // Line items matching the reference invoice
  const lineItems = [
    {
      label:   'Rental',
      unit:    planLabel.toLowerCase(),
      qty:     "-",
      rate:    fmtN(rentalRate),
      initial: fmtN(totalDailyAmt),
      adj:     '0.00',
      final:   fmtN(totalDailyAmt),
    },
    {
      label:   'Distance',
      unit:    d.distanceUOM ?? 'Km',
      qty:     "-",
      rate:    fmtN(d.distanceRate),
      initial: fmtN(totalDistAmt),
      adj:     '0.00',
      final:   fmtN(totalDistAmt),
    },
    {
      label:   'Engine Hours',
      unit:    'hours',
      qty:     "-",
      rate:    fmtN(d.engineRate),
      initial: fmtN(totalReeferAmt),
      adj:     '0.00',
      final:   fmtN(totalReeferAmt),
    },
    {
      label:   'Liability Insurance*:',
      unit:    'days',
      qty:     liabilityDays,
      rate:    fmtN(liabilityRate),
      initial: fmtN(liabilityAmt),
      adj:     '0.00',
      final:   fmtN(liabilityAmt),
    },
    {
      label:   'Physical Damage Insurance-:',
      unit:    'days',
      qty:     physicalDays,
      rate:    fmtN(physicalRate),
      initial: fmtN(physicalAmt),
      adj:     '0.00',
      final:   fmtN(physicalAmt),
    },
    {
      label:   `Platform Fee* (${d.platformFeesValue ?? ''})`,
      unit:    '-',
      qty:     '-',
      rate:    '0.00',
      initial: fmtN(platformFeesAmt),
      adj:     '0.00',
      final:   fmtN(platformFeesAmt),
    },
    {
      label:   'Concierge Fee**',
      unit:    '-',
      qty:     '-',
      rate:    '0.00',
      initial: '0.00',
      adj:     '0.00',
      final:   '0.00',
    },
    {
      label:   'Administration Fees:',
      unit:    '-',
      qty:     '-',
      rate:    '0.00',
      initial: fmtN(d.adminFees),
      adj:     '0.00',
      final:   fmtN(d.adminFees),
    },
    ...(d.customFields ?? []).map((cf) => ({
      label:   cf.fieldName,
      unit:    '-',
      qty:     1, // Assuming 1 unit based on logic
      rate:    fmtN(cf.amount),
      initial: fmtN(cf.amount),
      adj:     '0.00',
      final:   fmtN(cf.amount),
    })),
  ];

  // Filter out rows where Final is 0.00
  const filteredLineItems = lineItems.filter(item => item.final !== '0.00');

  const TD: React.CSSProperties = {
    border: '1px solid #999',
    padding: '5px 7px',
    fontSize: '11px',
    verticalAlign: 'top',
  };
  const TH: React.CSSProperties = {
    ...TD,
    fontWeight: 'bold',
    background: '#f5f5f5',
    textAlign: 'center',
  };

  return (
    <>
      <style>{`
        .cap-inv { font-family: Arial, Helvetica, sans-serif; font-size: 12px; background: #fff; padding: 18px 22px; }
        .cap-inv table { border-collapse: collapse; }
        .cap-inv .hdr-sep { border-top: 2px solid #555; margin: 10px 0 8px; }
        .cap-inv .foot-sep { border-top: 1px solid #555; margin: 10px 0 6px; }
        .cap-inv .lbl { font-size: 11px; color: #555; }
        .cap-inv .bold14 { font-weight: 700; font-size: 13px; }
        .cap-inv .right { text-align: right; }
        .cap-inv .center { text-align: center; }
      `}</style>

      <div className="cap-inv">

        {/* ── HEADER ── */}
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td style={{ width: '50%', verticalAlign: 'top' }}>
                <div className="lbl">Issue Date</div>
                <div style={{ fontWeight: 600 }}>{formatDateLong(invoiceDate)}</div>
                <br />
                <div className="lbl">Billed To:</div>
                <div className="bold14">{d.renterName || ''}</div>
                <div>{d.renterAddress || ''}</div>
              </td>
              <td style={{ width: '50%', verticalAlign: 'top', textAlign: 'right' }}>
                <img
                  src="../images/vHub-black.png"
                  alt="vHub Logo"
                  style={{ width: '120px', height: 'auto', objectFit: 'contain', marginBottom: '4px' }}
                />
                <div style={{ fontWeight: 700, fontSize: '12px' }}>vHub, a division of Finloc 2000 Inc</div>
                <div className="lbl">11505, 1ere ave, bureau 500 Saint-</div>
                <div className="lbl">Georges, QC G5Y 7X3 Canada</div>
                <div className="lbl" style={{ marginTop: '4px' }}>1 (866) 849-0189</div>
                <div className="lbl">www.vhubapp.com</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* <div className="hdr-sep" /> */}

        {/* ── INFO ROW ── */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <tbody>
            <tr>
              <td style={{ ...TD, width: '25%' }}>
                <strong>Invoice Number:</strong> {d.contractNumber ? `F-${d.contractNumber}-1` : '—'}<br />
                <strong>Reservation Code:</strong> {d.contractNumber || '—'}<br />
              </td>
              <td style={{ ...TD, width: '25%' }}>
                <strong>Owner:</strong> {d.owner ? `${d.owner}` : '—'}<br />
                <strong>Unit Number:</strong> {d.unitNumber || d.unitNumber || '—'}<br />
              </td>
              <td style={{ ...TD, width: '25%' }}>
                <strong>VIN:</strong> {d.vin || '—'}<br />
                <strong>Year:</strong> {d.year || '—'}
              </td>
              <td style={{ ...TD, width: '25%' }}>
                <strong>Equipment Type:</strong> {d.equipmentType || '—'}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── SIDE-BY-SIDE: SUMMARY (left) + CHARGES TABLE (right) ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0', marginBottom: '6px' }}>

          {/* LEFT — summary sidebar */}
          <table style={{ width: '15%', borderCollapse: 'collapse', flexShrink: 0, fontSize: '11px' }}>
            <tbody>
              {/* blank row to align with table header */}
              <tr><td colSpan={2} style={{ padding: '6px 0', fontWeight: 700, fontSize: '11px', background: 'none', border: 'none' }}>&nbsp;</td></tr>
              {[
                ['Duration:', `${billedDays} ${planLabel}`],
                ['Rate:',            fmtN(rentalRate)],
                // ['Estimated Daily Distance:', fmtN(avgDailyDist)],
                // ['Estimated Total Distance:', fmtN(totalDistance)],
                ['Distance Rate:',   fmtN(d.distanceRate)],
                ...((d.equipmentType || '').toLowerCase().includes('reefer') ? [
                  ['Estimated Daily Hours:',   fmtN(avgDailyHours)],
                  ['Estimate Total Hours:',    fmtN(totalEngineHours)],
                ] : []),
                ['Engine Rate:',      fmtN(d.engineDailyRate || d.engineWeeklyRate || d.engineMonthlyRate)],
                ['Liability Insurance:', `${fmtN(liabilityRate)}`],
                ['Physical Insurance:', `${fmtN(physicalRate)}`],
                ['Administration Fees:',         fmtN(d.adminFees)],
              ].filter(([_, val]) => val !== '0.00' && val !== '0' && !String(val).startsWith('0 '))
                .map(([label, val], i) => (
                <tr key={i}>
                  <td style={{ padding: '3px 6px', color: '#333', whiteSpace: 'nowrap' }}>{label}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* RIGHT — charges table (no internal cell borders) */}
          <table style={{ width: '85%', borderCollapse: 'collapse', border: '1px solid #999' }}>
            <thead>
              <tr>
                <th style={{ ...TH, width: '18%', textAlign: 'left', border: 'none', borderBottom: '1px solid #999' }}>Date</th>
                <th style={{ ...TH, width: '20%', textAlign: 'left', border: 'none', borderBottom: '1px solid #999' }}>Description</th>
                <th style={{ ...TH, width: '8%',  border: 'none', borderBottom: '1px solid #999', borderLeft: '1px solid #999' }}>Unit</th>
                <th style={{ ...TH, width: '8%',  border: 'none', borderBottom: '1px solid #999' }}>Qty</th>
                <th style={{ ...TH, width: '8%', textAlign: 'right',  border: 'none', borderBottom: '1px solid #999' }}>Rate</th>
                <th style={{ ...TH, width: '13%', textAlign: 'right', border: 'none', borderBottom: '1px solid #999', borderLeft: '1px solid #999' }}>Initial Charge</th>
                <th style={{ ...TH, width: '13%', textAlign: 'right', border: 'none', borderBottom: '1px solid #999' }}>Adjustment</th>
                <th style={{ ...TH, width: '12%', textAlign: 'right', border: 'none', borderBottom: '1px solid #999' }}>Final</th>
              </tr>
            </thead>
            <tbody>
              {filteredLineItems.map((item, i) => {
                const rowBorder = '1px solid #e5e5e5';
                return (
                  <tr key={i}>
                    {i === 0 && (
                      <td
                        style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'start', verticalAlign: 'top', borderRight: '1px solid #999', borderBottom: rowBorder }}
                        rowSpan={filteredLineItems.length}
                      >
                        {formatDate(startDate)} to {formatDate(endDate)}
                      </td>
                    )}
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: rowBorder }}>{item.label}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'center', borderBottom: rowBorder, borderLeft: '1px solid #999' }}>{item.unit}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'center', borderBottom: rowBorder }}>{item.qty}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder }}>{item.rate}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder, borderLeft: '1px solid #999' }}>{item.initial}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder }}>{item.adj}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder, fontWeight: 600 }}>{item.final}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {[
                { label: 'SUB TOTAL:',         i: fmt(subTotal),    a: fmt(0),    f: fmt(subTotal)    },
                { label: 'TAX:',               i: fmt(totalTaxes), a: fmt(0),    f: fmt(totalTaxes)  },
                { label: 'TOTAL:',             i: fmt(totalAmount), a: fmt(0),    f: fmt(totalAmount) },
              ].map((row, idx) => (
                <tr key={idx}>
                  <td colSpan={2} style={{ border: 'none', padding: '3px 6px', fontSize: '11px', textAlign: 'right' }} />
                  <td style={{ border: 'none', padding: '3px 6px', fontSize: '11px', borderLeft: '1px solid #999' }} />
                  <td colSpan={2} style={{ border: 'none', padding: '3px 6px', fontWeight: 700, fontSize: '11px', textAlign: 'right' }}>{row.label}</td>
                  <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right', borderLeft: '1px solid #999', borderTop: '1px solid #e5e5e5' }}>{row.i}</td>
                  <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right', borderTop: '1px solid #e5e5e5' }}>{row.a}</td>
                  <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right', borderTop: '1px solid #e5e5e5', fontWeight: 700 }}>{row.f}</td>
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

export default CheckAvailabilityPreview
